import { forwardRef, useImperativeHandle, useRef } from 'react';
import { TextArea, TextAreaProps } from '@vp/swan';

export type ResizableTextAreaRef =
    | HTMLTextAreaElement & {
          resize: () => void;
          baseRef: () => HTMLTextAreaElement | null;
      };

/**
 * A resizable text area component that adjusts its height based on the content.
 *
 * IOS hack: The component uses a shadow textarea to calculate the scroll height and set the main textarea's height accordingly.
 */
export const ResizableTextArea = forwardRef<ResizableTextAreaRef, TextAreaProps>(
    (props: TextAreaProps, ref) => {
        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        const shadowTextAreaRef = useRef<HTMLTextAreaElement>(null);

        useImperativeHandle(ref, () => {
            const textAreaElement = textAreaRef.current;

            return {
                ...(textAreaElement || {}),
                focus: () => {
                    textAreaElement?.focus();
                },
                blur: () => {
                    textAreaElement?.blur();
                },
                resize: () => {
                    if (!textAreaElement) {
                        return;
                    }
                    const minHeight = shadowTextAreaRef.current
                        ? shadowTextAreaRef.current.scrollHeight
                        : 0;

                    textAreaElement.style.height = 'auto';
                    textAreaElement.style.height = `${Math.max(
                        minHeight,
                        textAreaElement.scrollHeight
                    )}px`;
                },
                baseRef: () => textAreaRef.current,
            } as ResizableTextAreaRef;
        });

        return (
            <>
                <TextArea
                    rows={props.rows}
                    pb={props.pb}
                    pr={props.pr}
                    pt={props.pt}
                    ref={shadowTextAreaRef}
                    data-testid="shadow-resizable-textarea"
                    style={{
                        ...props.style,
                        position: 'absolute',
                        left: '0px', // Align with main textarea
                        top: '0px',
                        visibility: 'hidden', // Visually hidden and disables interactions but still accessible for height calculation
                    }}
                    fullWidth={props.fullWidth}
                    value={props.placeholder}
                    aria-hidden="true"
                    onChange={() => {
                        // Noop
                    }}
                />
                <TextArea ref={textAreaRef} {...props} />
            </>
        );
    }
);
